import React from 'react';
import { UserAvatar } from '@/components/UserAvatar';
import { personalityMeta } from '@/lib/personalities';
import { PERSONALITY } from '@/constants/testIds';
import { Sparkles, Clock } from 'lucide-react';

/**
 * Visually distinct personality card — designed to be screenshot-friendly for sharing.
 */
export default function PersonalityCard({ personality }) {
    if (!personality.ready) {
        return (
            <div
                data-testid={PERSONALITY.card}
                className="rounded-3xl border border-dashed border-[var(--ns-border)] p-6 flex flex-col items-center justify-center text-center min-h-[260px]"
            >
                <UserAvatar name={personality.name} picture={personality.picture} size={48} />
                <div className="font-display text-lg font-semibold mt-3 text-[var(--ns-text)]">{personality.name}</div>
                <div className="text-xs text-[var(--ns-text3)] mt-1 inline-flex items-center gap-1">
                    <Clock size={11} /> Hasn&apos;t submitted preferences yet
                </div>
            </div>
        );
    }
    const meta = personalityMeta(personality.personality_name);
    const Icon = meta.icon;
    return (
        <div
            data-testid={PERSONALITY.card}
            className="relative rounded-3xl overflow-hidden text-white p-6 sm:p-7 min-h-[280px] flex flex-col"
            style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}DD)` }}
        >
            {/* Decorative pattern */}
            <div
                aria-hidden
                className="absolute -right-10 -bottom-10 opacity-15 pointer-events-none"
                style={{ fontSize: 220 }}
            >
                {meta.emoji}
            </div>

            <div className="flex items-center justify-between relative">
                <span className="text-[10px] uppercase tracking-[0.22em] opacity-90 font-medium inline-flex items-center gap-1.5">
                    <Sparkles size={12} /> Travel personality
                </span>
                <Icon size={20} className="opacity-90" />
            </div>

            <div className="mt-4 relative">
                <div data-testid={PERSONALITY.name} className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                    {personality.personality_name}
                </div>
                <div data-testid={PERSONALITY.tagline} className="mt-2 text-sm sm:text-base opacity-95 leading-relaxed italic">
                    “{personality.tagline}”
                </div>
            </div>

            <p className="mt-4 text-sm opacity-90 leading-relaxed relative line-clamp-4">{personality.description}</p>

            <div className="mt-auto pt-5 flex items-center gap-3 relative">
                <UserAvatar name={personality.name} picture={personality.picture} size={32} />
                <div className="text-sm font-medium">{personality.name}</div>
            </div>
        </div>
    );
}
